import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatRelative } from '../utils/dateFormat';

/**
 * Localized activity row renderer.
 *
 * Backend stores both a legacy `description` (free text) and a
 * structured `event` key + `event_params` dict (added in audit-event refactor).
 * Prefer the structured form when available — translates the
 * `audit.events.<event>` key with i18next, interpolating params. Falls back
 * to `description` for older records.
 */
const ActivityStream = ({ activities }) => {
  const { t } = useTranslation();

  const getIcon = (type) => {
    const icons = {
      created: '✨',
      updated: '✏️',
      deleted: '🗑️',
      user_created: '👤',
      order_created: '📦',
      payment_received: '💰',
      stock_updated: '📊',
      default: '📌',
    };
    return icons[type] || icons.default;
  };

  const renderText = (activity) => {
    // Structured event form takes precedence; description is the fallback.
    if (activity.event) {
      const fallback = activity.description || activity.event;
      return t(`audit.events.${activity.event}`, fallback, activity.event_params || {});
    }
    return activity.description || '';
  };

  // Compact: one line per activity (text left, who / when right), a fixed
  // height that scrolls, so it does not push the rest of the dashboard down.
  return (
    <div className="card p-4" data-testid="activity-stream">
      <h3 className="section-title mb-2 text-base">
        📋 {t('shared.activityStream.title')}
      </h3>

      {activities && activities.length > 0 ? (
        <ul className="max-h-56 overflow-y-auto divide-y divide-surface-100 dark:divide-surface-800">
          {activities.map((activity, index) => (
            <li key={activity._id || activity.id || index} className="flex items-center gap-2 py-1.5 text-sm">
              <span className="flex-shrink-0 text-base leading-none" aria-hidden="true">{getIcon(activity.action_type || activity.type)}</span>
              <span className="flex-1 min-w-0 truncate text-surface-900 dark:text-surface-50" title={renderText(activity)}>
                {renderText(activity)}
              </span>
              <span className="flex-shrink-0 text-xs text-surface-500 dark:text-surface-400 whitespace-nowrap">
                {activity.username || activity.user || ''}{activity.timestamp ? ` · ${formatRelative(activity.timestamp)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="empty-state">
          {t('shared.activityStream.empty')}
        </p>
      )}
    </div>
  );
};

export default ActivityStream;
